import { useEffect, useRef, useState } from 'react';
import { useMatchmakingHistory } from '../hooks/useMatchmakingHistory';
import { groupHistoryByPeriod } from '../utils/history.utils';
import { HistoryHeader } from './MatchmakingHistoryPopover/HistoryHeader';
import { HistoryContent } from './MatchmakingHistoryPopover/HistoryContent';
import { HistoryLoadMore } from './MatchmakingHistoryPopover/HistoryLoadMore';

export interface HistoryItem {
  period: 'today' | 'yesterday' | 'last7Days' | 'last30Days' | 'older';
  answers?: {
    answer_q1?: string | null;
    answer_q2?: string | null;
    answer_q3?: string | null;
  } | null;
  filters?: {
    state?: string | null;
    county?: string | null;
    address?: string | null;
    zipCode?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    serviceRange?: string[] | null;
    distanceMiles?: number | null;
  } | null;
  searchId: number;
  promptText: string;
  createdAtUtc: string;
  resultsCount?: number;
  client_context?: {
    reason?: string | null;
    age_range?: string | null;
    relationship?: string | null;
  } | null;
}

type MatchmakingHistoryPopoverProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectHistory: (item: HistoryItem) => void;
  advisorId: string | null;
  clientId: string | null;
  shouldFetch?: boolean;
  onFetchComplete?: () => void;
};

const MatchmakingHistoryPopover = ({
  isOpen,
  onClose,
  onSelectHistory,
  advisorId,
  clientId,
  shouldFetch = false,
  onFetchComplete,
}: MatchmakingHistoryPopoverProps) => {
  const [displayLimit, setDisplayLimit] = useState(5);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { history, isLoading } = useMatchmakingHistory({
    advisorId,
    clientId,
    isOpen,
    shouldFetch,
    onFetchComplete,
  });

  useEffect(() => {
    if (!isOpen) {
      setDisplayLimit(5);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const groupedHistory = groupHistoryByPeriod(history);
  const hasMoreToday = groupedHistory.todayItems.length > displayLimit;
  const hasMoreOlder = groupedHistory.olderItems.length > displayLimit;
  const hasMore = hasMoreToday || hasMoreOlder;

  const handleLoadMore = () => {
    setDisplayLimit((prev) => prev + 5);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute top-full right-0 mt-2 w-[400px] bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_24px_80px_rgba(15,23,42,0.12)] z-[999] max-h-[600px] flex flex-col"
    >
      <HistoryHeader onClose={onClose} />
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <HistoryContent
          isLoading={isLoading}
          history={history}
          displayLimit={displayLimit}
          onSelectHistory={onSelectHistory}
          onClose={onClose}
        />
      </div>
      {!isLoading && (
        <HistoryLoadMore hasMore={hasMore} onLoadMore={handleLoadMore} />
      )}
    </div>
  );
};

export default MatchmakingHistoryPopover;

